import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  useJob, 
  useMarkNavigating, 
  useMarkArrived, 
  useVerifyStartOtp,
  useSubmitChecklist,
  useRecordPayment,
  useDeletePayment,
  useMarkComplete,
  useVerifyCloseOtp
} from '@/hooks/useJobs';
import { useGeolocation } from '@/hooks/useGeolocation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { LoadingSpinner } from '@/components/shared/LoadingStates';
import IssueDescription from '@/components/shared/IssueDescription';
import { toast } from 'sonner';
import { jobsApi, uploadApi } from '@/lib/api';
import { 
  ArrowLeft, 
  NavigationArrow, 
  MapPin, 
  Phone, 
  CheckCircle,
  Package,
  ClipboardText,
  CurrencyInr,
  Eye,
  PencilSimple,
  Trash,
  ArrowClockwise,
  X
} from '@phosphor-icons/react';
import { JOB_STATUS } from '@/constants/status';
import useAppStore from '@/stores/appStore';

export const JobCard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: job, isLoading } = useJob(id);

  const viewAsRole = useAppStore((state) => state.viewAsRole);
  const viewAsUser = useAppStore((state) => state.viewAsUser);
  const clearViewAs = useAppStore((state) => state.clearViewAs);
  const currentUser = useAppStore((state) => state.currentUser);
  const tenantConfig = useAppStore((state) => state.tenantConfig);
  const brandColor = tenantConfig?.brand_colour || '#002FA7';
  
  const isImpersonated = currentUser?.role === 'super_admin' && viewAsRole === 'technician';
  
  const handleExitViewAs = () => {
    clearViewAs();
    navigate('/ops/admin');
  };
  const { getCurrentPosition, loading: gpsLoading } = useGeolocation();

  // State
  const [startOtp, setStartOtp] = useState('');
  const [closeOtp, setCloseOtp] = useState('');
  const [resendingOtp, setResendingOtp] = useState(false);
  const [resendingStartOtp, setResendingStartOtp] = useState(false);

  const handleResendOtp = async () => {
    setResendingOtp(true);
    try {
      await jobsApi.resendOtp(id);
      toast.success('Close OTP sent to customer via WhatsApp');
    } catch (e) {
      toast.error('Failed to resend Close OTP');
    } finally {
      setResendingOtp(false);
    }
  };

  const handleResendStartOtp = async () => {
    setResendingStartOtp(true);
    try {
      await jobsApi.resendStartOtp(id);
      toast.success('Start OTP re-sent to customer via WhatsApp');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resend Start OTP');
    } finally {
      setResendingStartOtp(false);
    }
  };
  const [checklistItems, setChecklistItems] = useState([]);
  const [faultNotes, setFaultNotes] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('upi');
  const [paymentRefText, setPaymentRefText] = useState('');
  const [paymentPhotoUrl, setPaymentPhotoUrl] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [editingPayment, setEditingPayment] = useState(false);
  const [paymentRotation, setPaymentRotation] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  // Mutations
  const markNavigating = useMarkNavigating();
  const markArrived = useMarkArrived();
  const verifyStartOtp = useVerifyStartOtp();
  const submitChecklist = useSubmitChecklist();
  const recordPayment = useRecordPayment();
  const deletePaymentMutation = useDeletePayment();
  const markComplete = useMarkComplete();
  const verifyCloseOtp = useVerifyCloseOtp();

  // Keep track of the last loaded job ID and status to prevent overwriting unsaved changes during background refetches
  const lastJobIdRef = useRef(null);
  const lastJobStatusRef = useRef(null);

  // Initialize checklist when job loads, status changes, or checklist becomes available
  useEffect(() => {
    if (!job) return;

    const idChanged = lastJobIdRef.current !== job.id;
    const statusChanged = lastJobStatusRef.current !== job.status;
    const isEmpty = checklistItems.length === 0;

    if (job.checklist && (idChanged || statusChanged || isEmpty)) {
      setChecklistItems(job.checklist.map(item => ({
        id: item.id,
        label: item.label,
        checked: item.is_checked || false,
        note: item.note || '',
        is_mandatory: item.is_mandatory || false,
      })));
      lastJobIdRef.current = job.id;
      lastJobStatusRef.current = job.status;
    }
  }, [job, checklistItems.length]);

  // Sync payment state when job payment record is loaded or updated
  useEffect(() => {
    if (job?.payment) {
      setPaymentAmount(job.payment.amount?.toString() || '');
      setPaymentMethod(job.payment.method || 'upi');
      
      let refText = '';
      let photoUrl = '';
      try {
        const parsed = JSON.parse(job.payment.reference);
        refText = parsed.ref || '';
        photoUrl = parsed.photo || '';
      } catch (e) {
        if (job.payment.reference?.startsWith('http')) {
          photoUrl = job.payment.reference;
          refText = 'Screenshot Proof';
        } else {
          refText = job.payment.reference || '';
        }
      }
      setPaymentRefText(refText);
      setPaymentPhotoUrl(photoUrl);
      setPaymentRotation(0);
    } else {
      setPaymentAmount('');
      setPaymentMethod('upi');
      setPaymentRefText('');
      setPaymentPhotoUrl('');
      setPaymentRotation(0);
    }
  }, [job?.payment]);

  const handleNavigating = async () => {
    try {
      await markNavigating.mutateAsync(id);
      toast.success('Customer notified that you are on the way');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleArrived = async () => {
    let gps = null;
    try {
      gps = await getCurrentPosition();
    } catch (error) {
      console.warn('Geolocation failed or rejected:', error);
      toast.info('GPS check skipped or failed.');
    }

    try {
      await markArrived.mutateAsync({ 
        id, 
        gps: { 
          lat: gps?.lat ?? null, 
          lng: gps?.lng ?? null, 
          bypass_geofence: true 
        } 
      });
      toast.success('Arrival recorded. Start OTP sent to customer.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark arrival');
    }
  };

  const handleVerifyStartOtp = async () => {
    if (startOtp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }

    try {
      await verifyStartOtp.mutateAsync({ id, otp: startOtp });
      toast.success('Job started successfully');
      setStartOtp('');
    } catch (error) {
      toast.error('Invalid OTP. Please try again.');
      setStartOtp('');
    }
  };

  const handleSubmitChecklist = async () => {
    const allMandatoryChecked = checklistItems
      .filter(item => item.is_mandatory)
      .every(item => item.checked);

    if (!allMandatoryChecked) {
      toast.error('Please complete all mandatory checklist items');
      return;
    }

    try {
      const formattedItems = checklistItems.map(item => ({
        checklist_item_id: item.id,
        is_checked: item.checked,
        note: item.note || null
      }));
      await submitChecklist.mutateAsync({ id, items: formattedItems });
      toast.success('Checklist saved successfully');
    } catch (error) {
      toast.error('Failed to save checklist');
    }
  };

  const handleRecordPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (paymentMethod === 'upi' && !paymentPhotoUrl) {
      toast.error('Please upload a screenshot for UPI payment');
      return;
    }

    const referencePayload = paymentMethod === 'upi'
      ? JSON.stringify({ ref: paymentRefText, photo: paymentPhotoUrl })
      : paymentRefText;

    try {
      await recordPayment.mutateAsync({
        id,
        payment: {
          amount: parseFloat(paymentAmount),
          method: paymentMethod,
          reference: referencePayload
        },
      });
      toast.success(editingPayment ? 'Payment updated successfully' : 'Payment recorded successfully');
      setEditingPayment(false);
    } catch (error) {
      toast.error('Failed to save payment details');
    }
  };

  const handleDeletePayment = async () => {
    const toastId = toast.loading('Deleting payment record...');
    try {
      await deletePaymentMutation.mutateAsync(id);
      toast.success('Payment record deleted', { id: toastId });
      setEditingPayment(false);
    } catch (error) {
      toast.error('Failed to delete payment', { id: toastId });
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingPhoto(true);
    const toastId = toast.loading('Uploading screenshot...');
    try {
      const url = await uploadApi.uploadFile(file);
      setPaymentPhotoUrl(url);
      toast.success('Screenshot uploaded successfully', { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error('Failed to upload screenshot', { id: toastId });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleComplete = async () => {
    let gps = null;
    try {
      gps = await getCurrentPosition();
    } catch (error) {
      console.warn('Geolocation failed or rejected:', error);
      toast.info('GPS check skipped or failed.');
    }

    try {
      await markComplete.mutateAsync({ 
        id, 
        data: { 
          gps: gps || { lat: null, lng: null }, 
          fault_notes: faultNotes 
        } 
      });
      toast.success('Job marked as complete. Close OTP sent to customer.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete job');
    }
  };

  const handleVerifyCloseOtp = async () => {
    if (closeOtp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }

    try {
      await verifyCloseOtp.mutateAsync({ id, otp: closeOtp });
      toast.success('Job closed successfully!');
      navigate('/ops/tech');
    } catch (error) {
      toast.error('Invalid OTP. Please try again.');
      setCloseOtp('');
    }
  };

  const openMaps = () => {
    if (job?.customer_lat !== undefined && job?.customer_lng !== undefined && job?.customer_lat !== null && job?.customer_lng !== null) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${job.customer_lat},${job.customer_lng}`, '_blank');
    } else {
      const address = job?.service_address || job?.customer_address;
      if (address) {
        const encodedAddress = encodeURIComponent(address);
        window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
      } else {
        toast.error('No address details or coordinates available for navigation.');
      }
    }
  };

  if (isLoading) return <LoadingSpinner fullScreen />;
  if (!job) return <div className="p-4">Job not found</div>;

  const isPending = job.status === JOB_STATUS.PENDING;
  const isNavigating = job.status === JOB_STATUS.NAVIGATING;
  const isArrived = job.status === JOB_STATUS.ARRIVED;
  const isInProgress = job.status === JOB_STATUS.IN_PROGRESS && !job.completed_at;
  const awaitingCloseOtp = job.completed_at && job.status !== JOB_STATUS.COMPLETED;

  return (
    <div className="min-h-screen bg-muted/30 pb-8">
      <div className="sticky top-0 z-50">
        {isImpersonated && (
          <div className="bg-amber-500 text-white px-4 py-2.5 flex items-center justify-between text-xs sm:text-sm font-semibold border-b border-amber-600">
            <div className="flex items-center gap-2">
              <Eye size={18} />
              <span>Viewing as Technician: {viewAsUser?.full_name}</span>
            </div>
            <button 
              onClick={handleExitViewAs}
              className="bg-white text-amber-700 px-3 py-1 rounded text-xs font-bold hover:bg-amber-50 transition-colors shrink-0"
            >
              Exit View-As
            </button>
          </div>
        )}

        {/* Header */}
        <header className="bg-white border-b border-border shadow-sm">
          <div className="p-4 flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/ops/tech')}
              data-testid="back-btn"
              className="text-slate-650 hover:text-slate-900"
            >
              <ArrowLeft size={24} />
            </Button>
            <div>
              <h1 className="text-xl font-black tracking-tight text-slate-900 flex items-center gap-2">
                {job.ticket_number}
                {job.priority === 'urgent' && (
                  <span className="px-2 py-0.5 bg-red-100 border border-red-200 text-red-700 text-[10px] font-black rounded-full uppercase tracking-wider">
                    Urgent
                  </span>
                )}
              </h1>
              <p className="text-xs font-semibold text-slate-500">{job.customer_name}</p>
            </div>
          </div>
        </header>
      </div>

      <div className="p-4 space-y-4">
        {/* Customer Info */}
        <Card className="p-5 border-l-4 border-y-slate-100 border-r-slate-100 shadow-sm" style={{ borderLeftColor: brandColor }} data-testid="customer-info-card">
          <h2 className="font-bold text-slate-900 border-b pb-2 mb-3">Customer Information</h2>
          <div className="space-y-3">
            <div>
              <p className="label-micro mb-1">Name</p>
              <p className="font-semibold">{job.customer_name}</p>
            </div>
            <div>
              <p className="label-micro mb-1">Phone</p>
              <a 
                href={`tel:${job.customer_phone}`} 
                className="text-primary font-semibold flex items-center gap-2"
                data-testid="call-customer-btn"
              >
                <Phone size={16} />
                {job.customer_phone}
              </a>
            </div>
            <div>
              <p className="label-micro mb-1">Address</p>
              <p className="text-sm mb-2">{job.service_address || job.customer_address || 'No address provided'}</p>
              <Button 
                onClick={openMaps} 
                size="sm" 
                variant="outline"
                className="w-full"
                data-testid="navigate-btn"
              >
                <NavigationArrow size={16} className="mr-2" />
                Open in Maps
              </Button>
            </div>
          </div>
        </Card>

        {/* Product & Issue Details */}
        <Card className="p-5 border-l-4 border-y-slate-100 border-r-slate-100 shadow-sm space-y-4" style={{ borderLeftColor: brandColor }} data-testid="product-info-card">
          {/* Product Header */}
          <div>
            <h2 className="font-bold text-slate-900 border-b pb-2 mb-3">Product Details</h2>
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
              <div className="p-2 bg-white rounded-md border border-slate-200 shrink-0">
                <Package size={22} className="text-slate-500" />
              </div>
              <div className="min-w-0">
                <p className="font-bold text-slate-900">{job.product_name}</p>
                <p className="text-xs text-slate-500 font-mono mt-0.5">Serial: {job.serial_number}</p>
              </div>
            </div>
          </div>

          {/* Issue Category */}
          <div>
            <p className="label-micro mb-2">Issue Category</p>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-md">
              <span className="w-2 h-2 rounded-full bg-red-400 shrink-0" />
              <span className="font-bold text-sm text-red-800">{job.issue_category}</span>
            </div>
          </div>

          {/* Parsed Issue Description */}
          <IssueDescription description={job.issue_description} />

          {/* Service Address */}
          {job.service_address && (
            <div>
              <p className="label-micro mb-2">Service Address</p>
              <div className="flex items-start gap-2 p-3 bg-emerald-50/50 border border-emerald-100 rounded-lg">
                <MapPin size={16} className="text-emerald-600 mt-0.5 shrink-0" />
                <p className="text-sm text-slate-700">{job.service_address}</p>
              </div>
            </div>
          )}
        </Card>

        {/* Action Flow */}
        {isPending && (
          <Card className="p-4 bg-blue-50 border-blue-200" data-testid="action-card">
            <Button 
              onClick={handleNavigating}
              disabled={markNavigating.isPending}
              className="w-full h-14 text-lg font-bold text-white shadow-md hover:opacity-90 transition-opacity"
              style={{ backgroundColor: brandColor }}
              data-testid="on-my-way-btn"
            >
              {markNavigating.isPending ? 'Updating...' : 'On My Way'}
            </Button>
          </Card>
        )}

        {isNavigating && (
          <Card className="p-4 bg-yellow-50 border-yellow-200" data-testid="action-card">
            <p className="text-sm text-muted-foreground mb-3 text-center">
              Customer has been notified that you're on the way
            </p>
            <Button 
              onClick={handleArrived}
              disabled={markArrived.isPending || gpsLoading}
              className="w-full h-14 text-lg font-bold text-white shadow-md hover:opacity-90 transition-opacity"
              style={{ backgroundColor: brandColor }}
              data-testid="i-have-arrived-btn"
            >
              {markArrived.isPending || gpsLoading ? 'Capturing GPS...' : 'I Have Arrived'}
            </Button>
          </Card>
        )}

        {isArrived && (
          <Card className="p-4 bg-green-50 border-green-200" data-testid="otp-verification-card">
            <h3 className="font-bold mb-2">Enter Start OTP</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Ask the customer for the 6-digit Start OTP sent to their WhatsApp
            </p>
            <div className="flex justify-center mb-4">
              <InputOTP 
                maxLength={6} 
                value={startOtp} 
                onChange={setStartOtp}
                data-testid="start-otp-input"
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} className="h-12 w-12 text-lg" />
                  <InputOTPSlot index={1} className="h-12 w-12 text-lg" />
                  <InputOTPSlot index={2} className="h-12 w-12 text-lg" />
                  <InputOTPSlot index={3} className="h-12 w-12 text-lg" />
                  <InputOTPSlot index={4} className="h-12 w-12 text-lg" />
                  <InputOTPSlot index={5} className="h-12 w-12 text-lg" />
                </InputOTPGroup>
              </InputOTP>
            </div>
            <Button 
              onClick={handleVerifyStartOtp}
              disabled={startOtp.length !== 6 || verifyStartOtp.isPending}
              className="w-full h-12 font-bold text-white shadow hover:opacity-90 transition-opacity"
              style={{ backgroundColor: brandColor }}
              data-testid="verify-start-otp-btn"
            >
              {verifyStartOtp.isPending ? 'Verifying...' : 'Start Job'}
            </Button>

            {/* Resend Start OTP fallback */}
            <div className="mt-3 pt-3 border-t border-green-200">
              <p className="text-xs text-muted-foreground mb-2 text-center">
                Customer didn't receive the OTP on WhatsApp?
              </p>
              <Button
                onClick={handleResendStartOtp}
                disabled={resendingStartOtp}
                variant="outline"
                size="sm"
                className="w-full font-bold text-xs"
                data-testid="resend-start-otp-btn"
              >
                {resendingStartOtp ? 'Sending...' : 'Resend Start OTP via WhatsApp'}
              </Button>
            </div>
          </Card>
        )}

        {isInProgress && (
          <>
            {/* Checklist */}
            <Card className="p-4" data-testid="checklist-card">
              <h2 className="font-bold mb-3 flex items-center gap-2">
                <ClipboardText size={20} />
                Service Checklist
              </h2>
              {checklistItems.length === 0 ? (
                <div className="py-4 text-center text-sm text-slate-400 italic">
                  No service checklist configured for this product category.<br />
                  <span className="text-xs">Ask your admin to set one up in Configuration.</span>
                </div>
              ) : (
                <div className="space-y-3">
                  {checklistItems.map((item, idx) => (
                    <div key={item.id} className="space-y-2">
                      <label className="flex items-start gap-3">
                        <Checkbox 
                          checked={item.checked}
                          onCheckedChange={(checked) => {
                            const newItems = [...checklistItems];
                            newItems[idx].checked = checked;
                            setChecklistItems(newItems);
                          }}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <p className="font-medium">{item.label}</p>
                          {item.is_mandatory && (
                            <span className="text-xs text-red-600">* Required</span>
                          )}
                        </div>
                      </label>
                      {item.checked && (
                        <Input
                          placeholder="Add notes (optional)"
                          value={item.note}
                          onChange={(e) => {
                            const newItems = [...checklistItems];
                            newItems[idx].note = e.target.value;
                            setChecklistItems(newItems);
                          }}
                          className="ml-8"
                        />
                      )}
                    </div>
                  ))}
                  <div className="pt-2">
                    <Button 
                      onClick={handleSubmitChecklist}
                      disabled={submitChecklist.isPending}
                      className="w-full text-xs font-bold text-white uppercase tracking-wider h-10"
                      style={{ backgroundColor: brandColor }}
                    >
                      {submitChecklist.isPending ? 'Saving Checklist...' : 'Save Checklist'}
                    </Button>
                  </div>
                </div>
              )}
            </Card>

            {/* Fault Notes */}
            <Card className="p-4" data-testid="fault-notes-card">
              <Label className="font-bold mb-2 block">Work Done / Fault Notes</Label>
              <Textarea
                placeholder="Describe the work performed, parts replaced, etc."
                value={faultNotes}
                onChange={(e) => setFaultNotes(e.target.value)}
                rows={4}
                data-testid="fault-notes-input"
              />
            </Card>

            {/* Payment */}
            <Card className="p-4 border-2 border-slate-200 shadow-sm" data-testid="payment-card">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold flex items-center gap-2 text-slate-800">
                  <CurrencyInr size={22} className="text-primary" />
                  Payment Collection
                </h2>
                {job.payment && !editingPayment && (
                  <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-green-100 text-green-800 border border-green-200 animate-pulse">
                    Recorded
                  </span>
                )}
              </div>

              {job.payment && !editingPayment ? (
                // Display recorded payment summary
                <div className="space-y-4 pt-2">
                  <div className="grid grid-cols-2 gap-4 bg-slate-50 p-3.5 rounded-lg border border-slate-100">
                    <div>
                      <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Amount Collected</p>
                      <p className="text-lg font-extrabold text-slate-900">₹{parseFloat(job.payment.amount).toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Method</p>
                      <p className="text-sm font-bold text-slate-800 uppercase flex items-center gap-1.5 mt-0.5">
                        <span className={`h-2.5 w-2.5 rounded-full ${job.payment.method === 'upi' ? 'bg-indigo-500' : 'bg-amber-500'}`} />
                        {job.payment.method}
                      </p>
                    </div>
                  </div>

                  {paymentRefText && (
                    <div className="px-1">
                      <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Reference / Notes</p>
                      <p className="text-sm font-medium text-slate-700 mt-0.5">{paymentRefText}</p>
                    </div>
                  )}                   {job.payment.method === 'upi' && paymentPhotoUrl && (
                    <div className="px-1">
                      <div className="flex items-center justify-between max-w-xs mb-1.5">
                        <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Screenshot Proof</p>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-slate-500 hover:text-slate-900 rounded-full"
                          onClick={() => setPaymentRotation(r => (r + 90) % 360)}
                          title="Rotate screenshot"
                        >
                          <ArrowClockwise size={12} weight="bold" />
                        </Button>
                      </div>
                      <div className="relative group overflow-hidden rounded-lg border border-slate-200 max-w-xs bg-slate-100">
                        <img 
                          src={paymentPhotoUrl} 
                          alt="Payment proof" 
                          className="w-full h-auto max-h-48 object-cover transition-all duration-205"
                          style={{ transform: `rotate(${paymentRotation}deg)` }}
                        />
                        <button 
                          type="button"
                          onClick={() => setIsLightboxOpen(true)}
                          className="absolute inset-0 w-full h-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-bold gap-1 cursor-pointer focus:outline-none"
                        >
                          <Eye size={16} /> View Full Image
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setEditingPayment(true)}
                      className="flex-1 text-slate-700 font-semibold border-slate-200 hover:bg-slate-50"
                      data-testid="edit-payment-btn"
                    >
                      <PencilSimple size={16} className="mr-1.5" />
                      Edit Details
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={handleDeletePayment}
                      disabled={deletePaymentMutation.isPending}
                      className="flex-1 font-semibold"
                      data-testid="delete-payment-btn"
                    >
                      <Trash size={16} className="mr-1.5" />
                      {deletePaymentMutation.isPending ? 'Deleting...' : 'Delete'}
                    </Button>
                  </div>
                </div>
              ) : (
                // Display payment collection input form
                <div className="space-y-4 pt-1">
                  <div>
                    <Label className="text-xs font-bold text-slate-600 mb-1 block">Amount to Collect</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium">₹</span>
                      <Input
                        type="number"
                        placeholder="0.00"
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        className="pl-7 font-bold text-base"
                        data-testid="payment-amount-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs font-bold text-slate-600 mb-1 block">Payment Method</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={paymentMethod === 'upi' ? 'default' : 'outline'}
                        onClick={() => setPaymentMethod('upi')}
                        className={`flex-1 font-bold ${paymentMethod === 'upi' ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm' : ''}`}
                        data-testid="payment-method-upi"
                      >
                        UPI
                      </Button>
                      <Button
                        type="button"
                        variant={paymentMethod === 'cash' ? 'default' : 'outline'}
                        onClick={() => setPaymentMethod('cash')}
                        className={`flex-1 font-bold ${paymentMethod === 'cash' ? 'bg-amber-600 text-white hover:bg-amber-700 shadow-sm' : ''}`}
                        data-testid="payment-method-cash"
                      >
                        Cash
                      </Button>
                    </div>
                  </div>

                  {paymentMethod === 'upi' ? (
                    <div className="space-y-3 p-3 bg-indigo-50/50 rounded-lg border border-indigo-100/50">
                      <div>
                        <Label className="text-xs font-bold text-slate-600 mb-1 block">UPI Reference Number</Label>
                        <Input
                          type="text"
                          placeholder="e.g., UTR / Transaction ID"
                          value={paymentRefText}
                          onChange={(e) => setPaymentRefText(e.target.value)}
                          data-testid="payment-ref-input"
                          className="bg-white"
                        />
                      </div>

                      <div>
                        <Label className="text-xs font-bold text-slate-600 mb-1 block">Screenshot Proof</Label>
                        <div className="space-y-2">
                          <Input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={handlePhotoUpload}
                            disabled={uploadingPhoto}
                            data-testid="payment-photo-input"
                            className="bg-white text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 cursor-pointer"
                          />
                          {uploadingPhoto && (
                            <div className="flex items-center gap-1.5 text-xs text-indigo-600 font-medium">
                              <span className="h-3 w-3 animate-spin rounded-full border border-indigo-600 border-t-transparent mr-1" />
                              <span>Uploading screenshot...</span>
                            </div>
                          )}
                          {!uploadingPhoto && paymentPhotoUrl && (
                            <div className="mt-2 relative inline-block rounded-lg overflow-hidden border border-slate-200 bg-white">
                              <img 
                                src={paymentPhotoUrl} 
                                alt="Proof upload preview" 
                                className="h-20 w-auto object-contain"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Label className="text-xs font-bold text-slate-600 mb-1 block">Reference / Notes (Optional)</Label>
                      <Input
                        type="text"
                        placeholder="e.g., Received by hand"
                        value={paymentRefText}
                        onChange={(e) => setPaymentRefText(e.target.value)}
                        data-testid="payment-ref-input"
                      />
                    </div>
                  )}

                  <div className="flex gap-2 pt-2">
                    {editingPayment && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setEditingPayment(false);
                          if (job.payment) {
                            setPaymentAmount(job.payment.amount?.toString() || '');
                            setPaymentMethod(job.payment.method || 'upi');
                          }
                        }}
                        className="flex-1 font-semibold"
                        data-testid="cancel-payment-btn"
                      >
                        Cancel
                      </Button>
                    )}
                    <Button 
                      onClick={handleRecordPayment}
                      disabled={recordPayment.isPending || uploadingPhoto || (paymentMethod === 'upi' && !paymentPhotoUrl)}
                      className={`font-bold text-white shadow hover:opacity-90 transition-opacity ${editingPayment ? 'flex-1' : 'w-full'}`}
                      style={{ backgroundColor: brandColor }}
                      data-testid="record-payment-btn"
                    >
                      {recordPayment.isPending ? 'Saving...' : editingPayment ? 'Save Changes' : 'Record Payment'}
                    </Button>
                  </div>
                </div>
              )}
            </Card>

            {/* Complete Job */}
            <Card className="p-4 bg-green-50 border-green-200" data-testid="complete-card">
              <Button 
                onClick={handleComplete}
                disabled={markComplete.isPending || gpsLoading}
                className="w-full h-14 text-lg font-bold text-white shadow-md hover:bg-emerald-700 transition-colors bg-emerald-600"
                data-testid="job-done-btn"
              >
                <CheckCircle size={24} className="mr-2" />
                {markComplete.isPending || gpsLoading ? 'Capturing GPS...' : 'Job Done'}
              </Button>
            </Card>
          </>
        )}

        {awaitingCloseOtp && (
          <Card className="p-4 bg-blue-50 border-blue-200" data-testid="close-otp-card">
            <h3 className="font-bold mb-2">Enter Close OTP</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Ask the customer for the 6-digit Close OTP sent to their WhatsApp
            </p>
            <div className="flex justify-center mb-4">
              <InputOTP 
                maxLength={6} 
                value={closeOtp} 
                onChange={setCloseOtp}
                data-testid="close-otp-input"
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} className="h-12 w-12 text-lg" />
                  <InputOTPSlot index={1} className="h-12 w-12 text-lg" />
                  <InputOTPSlot index={2} className="h-12 w-12 text-lg" />
                  <InputOTPSlot index={3} className="h-12 w-12 text-lg" />
                  <InputOTPSlot index={4} className="h-12 w-12 text-lg" />
                  <InputOTPSlot index={5} className="h-12 w-12 text-lg" />
                </InputOTPGroup>
              </InputOTP>
            </div>
            <Button 
              onClick={handleVerifyCloseOtp}
              disabled={closeOtp.length !== 6 || verifyCloseOtp.isPending}
              className="w-full h-12 font-bold text-white shadow hover:opacity-90 transition-opacity"
              style={{ backgroundColor: brandColor }}
              data-testid="verify-close-otp-btn"
            >
              {verifyCloseOtp.isPending ? 'Verifying...' : 'Close Job'}
            </Button>
            <Button
              onClick={handleResendOtp}
              disabled={resendingOtp}
              variant="outline"
              className="w-full mt-3 h-10 text-xs font-bold border-blue-300 text-blue-700 bg-white hover:bg-blue-100 flex items-center justify-center gap-1.5"
            >
              <Phone size={14} /> {resendingOtp ? 'Resending...' : 'Resend OTP to Customer'}
            </Button>
          </Card>
        )}
      </div>

      {/* Fullscreen Image Lightbox Overlay */}
      {isLightboxOpen && paymentPhotoUrl && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/95 p-4 backdrop-blur-sm animate-fade-in">
          {/* Header Controls */}
          <div className="absolute top-4 right-4 flex items-center gap-3">
            <Button
              type="button"
              variant="ghost"
              className="h-9 px-3 text-white bg-white/10 hover:bg-white/20 hover:text-white rounded-full flex items-center gap-1.5 text-xs font-semibold"
              onClick={() => setPaymentRotation(r => (r + 90) % 360)}
            >
              <ArrowClockwise size={16} weight="bold" />
              Rotate
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="h-9 w-9 text-white bg-white/10 hover:bg-white/20 hover:text-white rounded-full flex items-center justify-center"
              onClick={() => setIsLightboxOpen(false)}
            >
              <X size={20} weight="bold" />
            </Button>
          </div>
          {/* Image Container */}
          <div className="max-w-full max-h-[85vh] flex items-center justify-center overflow-hidden transition-all duration-300">
            <img
              src={paymentPhotoUrl}
              alt="Payment proof fullscreen"
              className="max-w-[90vw] max-h-[80vh] object-contain rounded-lg transition-transform duration-200 ease-out"
              style={{ transform: `rotate(${paymentRotation}deg)` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};